import { Button, Flex, InputNumber, Table, Tooltip, Typography, theme } from 'antd';
import { LuPlus, LuTrash2, LuTriangleAlert } from 'react-icons/lu';

import ColorDot from './ColorDot';

const { Text } = Typography;

/**
 * Matrix (2D) classification editor.
 *
 * Model: `bands` (rows) are nearest-neighbor buckets of the band property; each
 * band has a `thresholds[i] = { min, boundaries[], max }` row over the threshold
 * property, where `boundaries` has one entry per class (the class's upper limit
 * in that band). So classes are the columns of the threshold grid.
 *
 * Scope: this component is now only the threshold grid. Everything else that used
 * to sit here moved to where it reads better —
 *   - the band/threshold property pickers to the drawer header (FilterEditorHeader),
 *     since they're the two axes the whole grid derives from;
 *   - class identity, pricing, and add/remove to CostSummary in the pane beside
 *     this one, so the grid that sets a class's *ranges* sits directly next to the
 *     table showing what those ranges *cost*;
 *   - the currency picker to that same cost summary header, next to the figures
 *     it denominates.
 *
 * So this editor only ever *reads* `classes`, to derive the grid's columns; it
 * never mutates them.
 *
 * The grid is a two-column antd Table: a pinned "Band" column and a "Class
 * ranges" column holding min, each class's upper boundary, and max as one run of
 * inputs per row, with per-input subtitles in the header. One row per band; the
 * ranges column scrolls horizontally.
 *
 * @param {object} props
 * @param {object} props.filter - matrix filter config
 * @param {(updates: object) => void} props.updateFilter
 * @param {Array<{id,label}>} [props.availableProperties] - property catalog,
 *   used to title the two columns after the selected band/threshold properties
 * @param {string} [props.coverageMessage] - threshold coverage-gap warning
 */
const MatrixEditor = ({ filter, updateFilter, availableProperties = [], coverageMessage }) => {
	const { token } = theme.useToken();
	const { bands = [], thresholds = [], classes = [] } = filter;

	// Column titles follow the axis pickers in the drawer header, so the grid
	// says "Height" rather than the generic "Band" once a property is chosen —
	// the header is what tells you what the numbers in each column *are*. The
	// label is used as-is: property labels already carry their unit, so appending
	// `unit` here would print it twice. Falls back to the generic name while
	// nothing is selected (or the property has dropped out of the catalog).
	const propertyTitle = (propertyId, fallback) =>
		availableProperties.find(p => p.id === propertyId)?.label ?? fallback;
	const bandTitle = propertyTitle(filter.bandProperty, 'Band');
	const rangesTitle = propertyTitle(filter.thresholdProperty, 'Class ranges');

	// --- Bands (rows) ---
	const updateBand = (index, value) =>
		updateFilter({ bands: bands.map((b, i) => (i === index ? value : b)) });

	const updateThreshold = (index, patch) =>
		updateFilter({
			thresholds: thresholds.map((t, i) => (i === index ? { ...t, ...patch } : t)),
		});

	const updateBoundary = (rowIndex, classIndex, value) =>
		updateThreshold(rowIndex, {
			boundaries: (thresholds[rowIndex]?.boundaries ?? []).map((b, i) =>
				i === classIndex ? value : b
			),
		});

	const addBand = () => {
		const nextBand = bands.length ? bands[bands.length - 1] + 1 : 1;
		updateFilter({
			bands: [...bands, nextBand],
			thresholds: [...thresholds, { min: 0, boundaries: classes.map(() => 0), max: null }],
		});
	};

	const removeBand = index =>
		updateFilter({
			bands: bands.filter((_, i) => i !== index),
			thresholds: thresholds.filter((_, i) => i !== index),
		});

	// Grid: exactly two columns. "Band" is pinned left. "Class ranges" holds the
	// band's whole threshold row — Min, one upper boundary per class, Max — plus
	// the row's remove action, as one non-wrapping run of inputs in a single cell.
	// Its header is two lines: the column title centred, and beneath it a subtitle
	// per input (Min / swatch + class / Max) laid out on the same fixed widths and
	// gap as the inputs, so each subtitle sits directly over its column of values
	// and a class is named once rather than beside every input in every row.
	const RANGE_GAP = 6;
	// Every range input starts at this width and grows equally from there
	// (`flex: 1 1 auto`). The table is `min-width: 100%` and `width: max-content`,
	// so while the inputs fit they share the column's width evenly, and once
	// adding classes pushes their combined base width past the pane, the table
	// grows and scrolls horizontally instead of squeezing them.
	const RANGE_INPUT_WIDTH = 96;
	const ACTION_WIDTH = 32;
	const rangeInputStyle = {
		width: RANGE_INPUT_WIDTH,
		minWidth: RANGE_INPUT_WIDTH,
		flex: '1 1 auto',
	};
	const actionStyle = { width: ACTION_WIDTH, flex: '0 0 auto' };

	const rangeSubtitles = [
		{ key: 'min', label: 'Min' },
		...classes.map((cls, ci) => ({
			key: cls.id,
			// Swatch + label, so a grid column is recognizable as the same class the
			// cost summary prices in the pane beside it.
			label: (
				<>
					<ColorDot color={cls.color} />≤ {cls.label || `C${ci + 1}`}
				</>
			),
		})),
		{ key: 'max', label: 'Max' },
		{ key: 'actions', label: '', action: true },
	];

	const columns = [
		{
			title: bandTitle,
			key: 'band',
			fixed: 'left',
			width: 150,
			render: (_, row) => (
				<InputNumber
					size="small"
					value={bands[row.i]}
					onChange={v => updateBand(row.i, v)}
					style={{ width: '100%' }}
					aria-label={`${bandTitle} value for row ${row.i + 1}`}
				/>
			),
		},
		{
			key: 'ranges',
			title: (
				<Flex vertical gap={4}>
					{/* Ruled like a real two-level header: a hairline under the title and
					    a box around each subtitle, so the header reads as "Class ranges"
					    over its sub-columns rather than as loose text. */}
					<Text
						strong
						style={{
							display: 'block',
							textAlign: 'center',
							fontSize: '0.8rem',
							paddingBottom: 4,
							borderBottom: `1px solid ${token.colorBorderSecondary}`,
						}}>
						{rangesTitle}
					</Text>
					<Flex gap={RANGE_GAP} wrap={false} style={{ width: '100%' }}>
						{rangeSubtitles.map(sub => (
							<Text
								key={sub.key}
								type="secondary"
								ellipsis={{ tooltip: sub.label }}
								style={{
									...(sub.action ? actionStyle : rangeInputStyle),
									fontSize: '0.72rem',
									fontWeight: 'normal',
									whiteSpace: 'nowrap',
									textAlign: 'center',
									padding: '0 4px',
									borderRadius: token.borderRadiusSM,
									// The empty action slot stays unboxed — there's nothing to label.
									border: sub.action
										? 'none'
										: `1px solid ${token.colorBorderSecondary}`,
								}}>
								{sub.label}
							</Text>
						))}
					</Flex>
				</Flex>
			),
			render: (_, row) => {
				const bandName = bands[row.i] ?? row.i + 1;
				return (
					<Flex align="center" gap={RANGE_GAP} wrap={false} style={{ width: '100%' }}>
						<InputNumber
							size="small"
							value={thresholds[row.i]?.min}
							onChange={v => updateThreshold(row.i, { min: v })}
							style={rangeInputStyle}
							aria-label={`Minimum for band ${bandName}`}
						/>
						{classes.map((cls, ci) => {
							const name = cls.label || `C${ci + 1}`;
							return (
								<InputNumber
									key={cls.id}
									size="small"
									value={thresholds[row.i]?.boundaries?.[ci]}
									onChange={v => updateBoundary(row.i, ci, v)}
									style={rangeInputStyle}
									aria-label={`Upper boundary of ${name} for band ${bandName}`}
								/>
							);
						})}
						<InputNumber
							size="small"
							value={thresholds[row.i]?.max}
							onChange={v => updateThreshold(row.i, { max: v })}
							placeholder="∞"
							style={rangeInputStyle}
							aria-label={`Maximum for band ${bandName} (empty = no limit)`}
						/>
						<Tooltip title="Remove band">
							<Button
								type="text"
								size="small"
								icon={<LuTrash2 />}
								onClick={() => removeBand(row.i)}
								aria-label={`Remove band ${bandName}`}
								style={actionStyle}
							/>
						</Tooltip>
					</Flex>
				);
			},
		},
	];

	const dataSource = bands.map((_, i) => ({ key: i, i }));

	return (
		<Flex vertical gap={16}>
			{/* Threshold grid */}
			<Flex vertical gap={8}>
				<Text strong style={{ fontSize: '0.8rem' }}>
					Threshold grid
				</Text>
				{classes.length === 0 && (
					<Text type="secondary" style={{ fontSize: '0.75rem' }}>
						No classes yet — add one from the Cost Summary to start building the grid.
					</Text>
				)}
				<Table
					size="small"
					bordered
					columns={columns}
					dataSource={dataSource}
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>
				<Button size="small" icon={<LuPlus />} onClick={addBand} block>
					Add band
				</Button>
				{/* Coverage gaps are gaps in these threshold ranges, so the warning sits
				    under the grid it refers to — after the values that caused it, rather
				    than competing with the section heading above them. */}
				{coverageMessage && (
					<Text type="warning" style={{ fontSize: '0.72rem' }}>
						<LuTriangleAlert style={{ verticalAlign: 'middle', marginRight: 4 }} />
						{coverageMessage}
					</Text>
				)}
			</Flex>
		</Flex>
	);
};

export default MatrixEditor;
